import QuestionLabel from './QuestionLabel';
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../../../lib/CurrencyHelpers.js';
import { minutesToDuration } from '../../../lib/DateTime.js';
import { CheckIcon } from '@heroicons/react/24/outline';
import Stepper from '../../Stepper.jsx';
import SelectItemButton from './components/SelectItemButton.jsx';
import { FormPart, part } from '~/lib/FormCssApi';

const AddonButton = ({
    addon,
    onClick,
    quantity,
    onChangeQuantity,
    enableQuantity,
    isSelected,
    layout,
    maxQuantity,
}) => {
    const multiplyQuantity = Math.max(1, quantity);
    return (
        <SelectItemButton
            onClick={onClick}
            quantity={quantity}
            onChangeQuantity={onChangeQuantity}
            enableQuantity={enableQuantity}
            isSelected={isSelected}
            quantityIncrement={addon.quantityIncrement}
            maxQuantity={maxQuantity}
            layout={layout}
            className="!mb-0"
            rightContent={
                <div
                className={layout === 'grid'
                    ? 'flex gap-4 @sm:flex-col @sm:gap-0'
                    : 'flex gap-4 @sm:flex-col @sm:gap-0 @md:items-end'
                    }
                >
                    {addon.price > 0 && (
                        <div {...part(FormPart.SELECTION_PRICE)}>
                            {formatCurrency(
                                addon.price * Math.max(1, quantity),
                            )}
                        </div>
                    )}
                    {addon.duration > 0 && (
                        <div {...part(FormPart.SELECTION_PRICE)}>
                            +
                            {minutesToDuration(
                                addon.duration * Math.max(1, quantity),
                            )}
                        </div>
                    )}
                </div>
            }
        >
            <div
                {...part(FormPart.SELECTION_TITLE)}
                className="font-semibold leading-tight"
            >
                {addon.name}
            </div>
            {!!addon.description && (
                <div
                    {...part(FormPart.SELECTION_DESCRIPTION)}
                    className="whitespace-pre-line text-gray-500 text-sm"
                >
                    {addon.description}
                </div>
            )}
        </SelectItemButton>
    );
};

const AddonQuestion = ({ label, value, setValue, settings }) => {
    const addons = settings?.addons || [];

    useEffect(() => {
        // Update price!
        const currentPrice = value?.price;
        let newPrice = 0;
        for (const selection of value?.addons || []) {
            newPrice += selection.addon.price * selection.quantity;
        }

        if (currentPrice !== newPrice) {
            setValue({ ...value, price: newPrice });
        }
    }, [JSON.stringify(value)]);

    function toggleAddon(addon) {
        const idx = findAddon(addon);
        if (idx > -1) {
            // remove
            const newAddons = [...value.addons];
            newAddons.splice(idx, 1);
            setValue({
                ...value,
                addons: newAddons,
                oncePerTransaction: settings?.oncePerTransaction,
            });
        } else {
            // Add
            setValue({
                ...value,
                addons: [
                    ...(value?.addons || []),
                    {
                        addon: addon,
                        quantity: 1,
                    },
                ],
                oncePerTransaction: settings?.oncePerTransaction,
            });
        }
    }

    function setAddonQuantity(addon, quantity) {
        if (quantity <= 0) {
            // Just remove
            return toggleAddon(addon);
        }
        if (addon.maxQuantity && quantity > addon.maxQuantity) {
            return;
        }
        const idx = findAddon(addon);
        if (idx < 0) {
            return;
        }
        const newAddons = [...value.addons];
        newAddons[idx].quantity = quantity;
        setValue({
            ...value,
            addons: newAddons,
            oncePerTransaction: settings?.oncePerTransaction,
        });
    }

    function findAddon(addon) {
        for (const i in value?.addons || []) {
            //console.log('checking if', addon, 'selected compared to', value.addons[i].addon)
            if (
                JSON.stringify(value.addons[i].addon) === JSON.stringify(addon)
            ) {
                return i;
            }
        }

        return -1;
    }

    return (
        <label>
            <QuestionLabel label={label} settings={settings} />
            <div className="@container">
            <div
                className={settings.layout === 'grid'
                    ? '@container grid @xs:grid-cols-2 gap-4'
                    : '@container grid gap-4'
                }
            >
                {addons.map((addon, idx) => {
                    const valueIdx = findAddon(addon);

                    return (
                        <AddonButton
                            key={idx}
                            addon={addon}
                            onClick={() => {
                                toggleAddon(addon);
                            }}
                            onChangeQuantity={newQuantity => {
                                setAddonQuantity(addon, newQuantity);
                            }}
                            quantity={
                                valueIdx > -1
                                    ? value?.addons[valueIdx]?.quantity
                                    : 0
                            }
                            isSelected={valueIdx > -1}
                            layout={settings?.layout}
                            enableQuantity={!!settings?.enableQuantity}
                            maxQuantity={addon.maxQuantity}
                        />
                    );
                })}
            </div>
            </div>
        </label>
    );
};

export default AddonQuestion;
